/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { IconMemo } from "@insite/mobius/Icon";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import ChevronRight from "@monsma/XDL/Icons/ChevronRight";
import uniqBy from "lodash/uniqBy";
import * as React from "react";
import styled, { css, withTheme } from "styled-components";

export type BreadcrumbsProps = MobiusStyledComponentProps<
    "nav",
    {
        /** CSS string or styled-components function to be injected into this component.
         * @themable */
        css?: StyledProp<BreadcrumbsProps>;
        /** An array of link props */
        links?: LinkProps[];
        /**
         * Indicates how the `css` property is combined with the variant `css` property from the theme.
         * If true, the variant css is applied first and then the component css is applied after causing
         * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
         */
        mergeCss?: boolean;
        /**
         * Optional props to be passed down to all Link and Typography components.
         * Can be overriden by individual typographyProps in the links array.
         * @themable */
        typographyProps?: TypographyPresentationProps;
    }
>;

export type BreadcrumbsPresentationProps = Omit<BreadcrumbsProps, "links">;

export const Slash = styled(Typography as any).attrs({ children: "/" })`
    margin: 0 10px;
    display: block;
`;

export const StyledNav = styled.nav.attrs({ "aria-label": "breadcrumbs" })`
    display: flex;
    flex-direction: row;
    align-items: center;
    ${injectCss}
`;

const Breadcrumbs: React.FC<BreadcrumbsProps> = withTheme(({ links, mergeCss, ...otherProps }) => {
    const { spreadProps, applyStyledProp } = applyPropBuilder(otherProps, { component: "breadcrumbs" });
    const typographyProps = spreadProps("typographyProps");
    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.breadcrumbs?.defaultProps?.mergeCss;
    // return null if `links` is an empty empty object, collection, map, or set.
    if (!links || links.length === 0) {
        return null;
    }
    const children = links.map((link: any, index: number) => {
        const children = link.children;
        // yield null if no children
        if (!React.Children.count(children)) {
            return null;
        }
        const key = typeof children === "string" ? `${children}_${index}` : index;
        if (index === links.length - 1) {
            return (
                <Typography mergeCss={resolvedMergeCss} {...typographyProps} aria-current="page" key={key} data-test-selector={`breadCrumb_${children}`}>
                    {children}
                </Typography>
            );
        }

        // xng-change: Replace the slash icon with chevron right one.
        return (
            <React.Fragment key={key}>
                <Link typographyProps={typographyProps} {...link} />
                <IconMemo
                    src={ChevronRight}
                    size={10}
                    color="common.ui13"
                    css={css`
                        margin: 0 10px;
                    `}
                />
            </React.Fragment>
        );
    });
    // return null if `links` map yields an array of nulls
    if (Object.keys(children.filter((x: typeof Link) => x !== null)).length === 0) {
        return null;
    }

    // xng-change
    const filteredChildren = uniqBy(children, (child: any) => {
        return child.key;
    });

    // xng-change
    const getPreparedChildren = (children: any) => {
        if (children.length <= 2) {
            return children;
        }

        const childrenWithTemplate = [];

        for (const [index, child] of children.entries()) {
            if (index === 0 || index === children.length - 1) {
                continue;
            }

            childrenWithTemplate.push(child);
        }

        return [
            children[0],
            <StyledWrapper className="breadcrumbs-overflow" key={uniqueId()}>
                <StyledWrapper className="breadcrumbs-overflow-items">{childrenWithTemplate}</StyledWrapper>
                <IconMemo
                    src={ChevronRight}
                    size={10}
                    color="common.ui13"
                    css={css`
                        margin: 0 10px 0 20px;
                    `}
                />
            </StyledWrapper>,
            children[children.length - 1],
        ];
    };

    const preparedChildren = getPreparedChildren(filteredChildren); // xng-change

    // xng-change
    return (
        <StyledNav {...otherProps} css={applyStyledProp("css", resolvedMergeCss)}>
            {preparedChildren}
        </StyledNav>
    );
});

Breadcrumbs.displayName = "Breadcrumbs";
/** @component */
export default Breadcrumbs;
